/**
 * Link a freehand team-member name to a real directory account. Shows a
 * one-click "Link to X" for a confident fuzzy match, plus a searchable picker
 * for anything else — a human always confirms (names are not unique).
 */

import { Button, Caption1, makeStyles, tokens } from '@fluentui/react-components'
import { PersonPicker } from './PersonPicker'
import { useToast } from './ToastProvider'
import { useLinkFreehandMember, type FreehandSuggestion } from '../hooks/useDemands'
import type { MentionableUser } from '../hooks/useMentionableUsers'
import { apiErrorMessage } from '../lib/apiError'

const useStyles = makeStyles({
  wrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    padding: '6px 8px',
    border: `1px dashed ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
  },
  head: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px' },
  name: { fontWeight: 600 },
  hint: { color: tokens.colorNeutralForeground3 },
})

interface Props {
  demandId: number
  name: string
  candidates: FreehandSuggestion['candidates']
  talentOptions: MentionableUser[]
}

export function FreehandLinkControl({ demandId, name, candidates, talentOptions }: Props) {
  const styles = useStyles()
  const link = useLinkFreehandMember()
  const toast = useToast()
  const best = candidates[0] // backend sorts best-first
  const hasConfident = !!best && best.score >= 2

  function doLink(talentId: number) {
    link.mutate(
      { demandId, freehandName: name, talentId },
      {
        onSuccess: () => toast.showToast({ title: `Linked "${name}" to their account` }),
        onError: (e) =>
          toast.showToast({ title: apiErrorMessage(e, 'Link failed'), intent: 'error' }),
      },
    )
  }

  return (
    <div className={styles.wrap} data-testid={`freehand-link-${name}`}>
      <div className={styles.head}>
        <span className={styles.name}>{name}</span>
        <Caption1 className={styles.hint}>freehand · no account</Caption1>
      </div>
      {hasConfident && (
        <Button
          size="small"
          appearance="primary"
          disabled={link.isPending}
          onClick={() => doLink(best.id)}
          data-testid={`freehand-link-suggest-${name}`}
        >
          Link to {best.name}
        </Button>
      )}
      <PersonPicker
        currentName={null}
        roleLabel={hasConfident ? 'or pick another account' : `Link "${name}" to account`}
        avatarColor="#0f766e"
        users={talentOptions}
        disabled={link.isPending}
        onSelect={(u) => {
          if (u) doLink(u.id)
        }}
      />
    </div>
  )
}
